import {AppLanguage, useDisplayMobile} from '@/logged/common/composables';
import {useNavigationTabs} from '@/logged/features/aside-menu/composables';
import {useHeaderNavigationTabs} from '@/logged/features/header-menu/composables';
import {useUser} from '@/logged/features/user';
import {useMediaQuery} from '@malt/joy-entrypoint/src/vue/use';
import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {useTourDefinition} from '.';

export const useOnboardingExistingUser = defineStore('onboarding-existing-user-store', () => {
    const userStore = useUser();
    const {filteredNavigationRoutes} = useNavigationTabs();
    const headerNavigationTabs = useHeaderNavigationTabs();
    const {isDisplayMobile} = useDisplayMobile();

    const allTourDefinition = useTourDefinition();
    const {matchMedia} = useMediaQuery();

    const selectedOnboardingTourStepIndex = ref(0);
    const selectedOnboardingTourStep = computed(() => activeTours.value[selectedOnboardingTourStepIndex.value]);
    const showExistingUserOnboardingTour = computed(
        () =>
            !!userStore.user?.featureFlags['navbar-onboarding'] &&
            !userStore.user?.options.newUser &&
            !userStore.user?.options.onboarded &&
            !isDisplayMobile.value,
    );

    const selectedIllustration = computed<string | undefined>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        const illustrations = selectedOnboardingTourStep.value.illustrations;
        return illustrations[selectedLanguage] ?? illustrations[AppLanguage.FR];
    });

    /** Used for prefetching */
    const filteredIllustrationsByLanguage = computed<string[]>(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        return allTourDefinition.value.map((m) => m.illustrations[selectedLanguage]).filter((f): f is string => !!f);
    });

    const allPresentationGifs: Partial<Record<AppLanguage, string>> = {
        [AppLanguage.FR]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_FR.gif',
        [AppLanguage.EN]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_EN.gif',
        [AppLanguage.ES]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_ES.gif',
        [AppLanguage.DE]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_DE.gif',
        [AppLanguage.NL]: 'https://dam.malt.com/navbar/onboarding/gifs/UI_Malt_16x9_NL.gif',
    };

    const displayedPresentationGif = computed(() => {
        const selectedLanguage = userStore.user?.currentLanguage as AppLanguage;
        return allPresentationGifs[selectedLanguage];
    });

    const activeTours = computed(() => {
        const activeNavbarIds = [...filteredNavigationRoutes.value, ...headerNavigationTabs.value].map((nav) => nav.id);
        return allTourDefinition.value.filter((tour) => activeNavbarIds.includes(tour.navTabId));
    });

    return {
        selectedOnboardingTourStep,
        selectedOnboardingTourStepIndex,
        selectedIllustration,
        showExistingUserOnboardingTour,
        displayedPresentationGif,
        activeTours,
        filteredIllustrationsByLanguage,
    };
});
